import { Dialog, SxProps, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid'
import { CSSProperties, useMemo, useState } from 'react'

import KanseiDataGrid from 'components/copied/dataGrid'
import ModalLayout from 'components/layouts/modalLayout'
import { helper } from 'helper'
import { WeekKey } from 'helper/week'
import SectionComponent from 'pages/securityField/settings/components/section'
import WorkPatternSettingModal from 'pages/securityField/settings/components/workPatternSettingModal'
import { WorkPatternContent } from 'types/workPattern'
import { colors, Icons } from 'variables'

interface Props {
  workPatterns: (WorkPatternContent & { id: string })[]
  onDelete: (ids: string[]) => void
  onAdd: (data: WorkPatternContent) => void
  onUpdate: (id: string, data: WorkPatternContent) => void
}

const workPatternColumns: GridColDef[] = [
  {
    field: 'editButton',
    headerName: '',
    align: 'center',
    sortable: false,
    width: 50,
  },
  {
    field: 'workPattern',
    headerName: '勤務パターン',
    width: 120,
  },
  {
    field: 'startTime',
    headerName: '開始時間',
    width: 100,
    renderCell: (params) => {
      return helper.formatter.durationFormat(params.value)
    },
  },
  {
    field: 'endTime',
    headerName: '終了時間',
    width: 100,
    renderCell: (params) => {
      return helper.formatter.durationFormat(params.value)
    },
  },
  {
    field: 'assignNum',
    headerName: '人数',
    width: 50,
  },
  {
    field: 'daysOfWeeks',
    headerName: '',
    flex: 1,
    renderCell: (params) => {
      const checked: boolean[] = params.value
      const activeDayOfWeeks = helper.week
        .dayOfWeeks()
        .filter((_, i) => checked[i])

      return (
        <Typography component="span">
          {activeDayOfWeeks.map((day, i) => {
            return (
              <Typography
                key={i}
                component="span"
                sx={{
                  ...weekStyle,
                  ...weekColorStyle(day),
                }}
              >
                {day}
              </Typography>
            )
          })}
        </Typography>
      )
      // return activeDayOfWeeks.join('　')
    },
  },
]

const sectionTitle = '勤務パターン設定'
export default function WorkPatternSettingComponent(props: Props) {
  const [openSettingModal, setOpenSettingModal] = useState<
    boolean | (WorkPatternContent & { id: string })
  >(false)
  const [checked, setChecked] = useState<string[]>([])

  const columns = useMemo(() => {
    return workPatternColumns.map((column) => {
      if (column.field === 'editButton') {
        return {
          ...column,
          renderCell: (params: any) => {
            return (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  setOpenSettingModal({
                    id: params.row.id,
                    name: params.row.workPattern,
                    startTime: helper.time.toMinutes(params.row.startTime),
                    endTime: helper.time.toMinutes(params.row.endTime),
                    assignNum: params.row.assignNum,
                    daysOfWeeks: params.row.daysOfWeeks,
                  })
                }}
              >
                <Icons.editButton />
              </IconButton>
            )
          },
        }
      } else {
        return column
      }
    })
  }, [props.workPatterns])

  const rows = useMemo(() => {
    return props.workPatterns.map((workPattern) => ({
      id: workPattern.id,
      workPattern: workPattern.name,
      startTime: helper.time.fromMinutes(workPattern.startTime),
      endTime: helper.time.fromMinutes(workPattern.endTime),
      assignNum: workPattern.assignNum,
      daysOfWeeks: workPattern.daysOfWeeks,
    }))
  }, [props.workPatterns])

  const handleCheckbox = (e: GridRowSelectionModel) => {
    setChecked(e as string[])
  }

  const handleAdd = () => {
    setOpenSettingModal(true)
  }

  const handleDelete = () => {
    props.onDelete(checked)
  }

  const handleModalCancel = () => {
    setOpenSettingModal(false)
  }

  const handleModalAdd = (data: WorkPatternContent) => {
    props.onAdd(data)
    setOpenSettingModal(false)
  }

  const handleModalUpdate = (id: string, data: WorkPatternContent) => {
    props.onUpdate(id, data)
    setOpenSettingModal(false)
  }

  const isOpenModal = () => {
    return openSettingModal !== false
  }

  const defaultValue = () => {
    if (typeof openSettingModal === 'object') {
      return openSettingModal
    }
  }

  return (
    <>
      <SectionComponent
        title={sectionTitle}
        actions={
          <>
            <Button variant="outlined" onClick={handleAdd}>
              <Icons.addButton />
              追加
            </Button>
            <Button variant="outlined" onClick={handleDelete}>
              <Icons.deleteButton />
              削除
            </Button>
          </>
        }
      >
        <KanseiDataGrid
          sx={dataGridStyle}
          rows={rows as []}
          columns={columns}
          handleChange={handleCheckbox}
        />
      </SectionComponent>
      <Dialog open={isOpenModal()}>
        <ModalLayout title={sectionTitle}>
          <WorkPatternSettingModal
            onCancel={handleModalCancel}
            onAdd={handleModalAdd}
            onUpdate={handleModalUpdate}
            defaultValue={defaultValue()}
          />
        </ModalLayout>
      </Dialog>
    </>
  )
}

const dataGridStyle: SxProps = {
  '& .MuiDataGrid-main': {
    height: '100%',
  },
  '& .MuiDataGrid-virtualScroller': {
    maxHeight: '400px',
    overflowY: 'scroll',
  },
}

const weekStyle: CSSProperties = {
  margin: '0 4px',
}

const weekColorStyle = (week: WeekKey): CSSProperties => {
  if (week === '日' || week === '祝') {
    return {
      // 日曜日
      color: colors.WEEK.SUN_COLOR,
    }
  }

  if (week === '土') {
    return {
      // 土曜日
      color: colors.WEEK.SAT_COLOR,
    }
  }

  return {
    // 平日 (色の上書き無し)
  }
}
